import { Spinner } from "@/components/spinner/spinner";
import { DashboardRoutes } from "@/constants/routes";
import { useAuth } from "@/context/auth/useAuth";
import { useFidexaPayAvailability } from "@/domain/fidexaPayWallet";
import { featuresService } from "@/services/featuresService";
import type { ReactNode } from "react";
import { Navigate, Outlet } from "react-router";

/**
 * Gate duplo do Fidexa Pay: permissão (features do usuário) E pelo menos uma
 * loja do usuário na allowlist da flag `fidexa_pay.module`. Mesmo desenho do
 * CampaignGuard.
 */

type FidexaPayGuardProps = {
  feature: string;
  children: ReactNode;
};

/** Versão para o menu: some silenciosamente quando o gate não passa. */
export const FidexaPayGuard = ({ feature, children }: FidexaPayGuardProps) => {
  const { featuresLoaded } = useAuth();
  const { availability } = useFidexaPayAvailability();

  if (!featuresLoaded) return null;
  if (!availability.enabled) return null;
  if (!featuresService.hasFeature(feature)) return null;

  return <>{children}</>;
};

type FidexaPayRouteProps = {
  feature: string;
  redirectTo?: string;
};

/**
 * Versão para o router: espera as features e a availability antes de
 * decidir (o acesso direto pela URL não redireciona antes da flag chegar).
 */
export const FidexaPayRoute = ({
  feature,
  redirectTo = DashboardRoutes.DASHBOARD,
}: FidexaPayRouteProps) => {
  const { featuresLoaded } = useAuth();
  const { availability, isLoadingAvailability } = useFidexaPayAvailability();

  if (!featuresLoaded || isLoadingAvailability) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!availability.enabled || !featuresService.hasFeature(feature)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
};
